import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { Theme, ThemeService } from '../../../../core/services/theme.service';

export interface ChartData {
  label: string;
  value: number;
  count?: number;
  color?: string;
  labelColor?: string;
  segments?: { label: string; value: number; color: string }[];
}

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None, // Allows global d3 styles if needed
})
export class DonutChartComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('chartContainer', { static: false })
  private chartContainer!: ElementRef;
  private themeService = inject(ThemeService);
  // Default data matching your image
  @Input() data: ChartData[] = [
    { label: 'Facebook', value: 529752, color: '#1877F2' }, // Blue
    { label: 'Instagram', value: 523441, color: '#E94057' }, // Pinkish Red
    { label: 'LinkedIn', value: 18000, color: '#0A66C2' }, // Darker Blue (Adjusted value to match visual 1.6%)
    { label: 'YouTube', value: 57000, color: '#FF0000' }, // Red (Adjusted value to match visual 4.9%)
  ];

  @Input() width = 500;
  @Input() height = 400;
  @Input() minHeight: number = 0;
  @Input() innerRadiusRatio: number = 0.5; // Ratio of radius for inner circle (0-1)
  @Input() outerRadiusRatio: number = 0.75; // Ratio of radius for outer circle (0-1)
  @Input() translateX: number = 0; // X offset for chart positioning
  @Input() translateY: number = 0; // Y offset for chart positioning
  @Input() radiusScale: number = 1; // Scale factor for radius (default 1 = use full available space)
  @Input() showLegend: boolean = false;
  @Input() strokeColor: string = '';
  @Input() legendPosition: 'left' | 'right' | 'top' | 'bottom' = 'right';
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  @Input() legendShape: 'circle' | 'square' = 'square';
  @Input() showLegendValue: boolean = true;
  @Input() showValue: boolean = true;
  @Input() showPolylines: boolean = true;
  // Place each label right next to its slice (along the slice's angle) instead of
  // pushing them to the far left/right edge of the chart. Useful when polylines are
  // hidden and you want the value sitting close to the donut.
  @Input() labelOnArc: boolean = false;
  @Input() variant: 'pie' | 'concentric' = 'pie';
  @Input() semiCircle: boolean = false;
  @Input() legendTextColor: string | null = null;
  @Input() roundedCaps: boolean = true;
  @Input() gapRatio: number = 0.3; // Ratio of space per ring dedicated to the gap (0-1)
  @Input() arcAngle: number = 360; // For partial concentric circle (e.g. 270)
  @Input() maxRingWidth?: number; // Optional max thickness of a ring
  @Input() labelFormat: 'default' | 'value-percentage' | 'percentage' = 'default';
  @Input() centerTitle?: string;
  @Input() centerValue?: string;
  @Input() expandOnHover: boolean = true;
  @Input() customTooltip?: (data: any, percent: string, colors: any) => string;
  // Label of the slice to highlight from outside the chart (e.g. hovering a
  // legend row elsewhere on the page). Set to null to clear the highlight.
  @Input() highlightedLabel: string | null = null;
  // Emits the hovered slice's label on mouseenter and null on mouseleave, so a
  // consumer can mirror the highlight onto a legend rendered outside the chart.
  @Output() sliceHover = new EventEmitter<string | null>();

  // Processed data with percentages for the legend
  public displayData: any[] = [];

  // Kept from the last pie-chart render so an external highlight (legend hover)
  // can restyle the matching slice without a full chart redraw.
  private renderedPaths?: d3.Selection<SVGPathElement, d3.PieArcDatum<ChartData>, SVGGElement, unknown>;
  private renderedArc?: d3.Arc<any, d3.PieArcDatum<ChartData>>;
  private renderedArcHover?: d3.Arc<any, d3.PieArcDatum<ChartData>>;

  constructor() { }
  ngOnInit(): void {
    this.processData();
  }

  ngAfterViewInit(): void {
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.createChart(); // Redraw chart with new theme colors
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.createChart(); // re-render chart with correct size
    });

    if (this.chartContainer?.nativeElement) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A legend row elsewhere on the page hovering in/out only touches
    // highlightedLabel — restyle the matching slice in place instead of
    // tearing down and re-running the whole D3 render (and its entry
    // animation) on every mouseenter/mouseleave.
    const changedKeys = Object.keys(changes);
    if (changedKeys.length === 1 && changedKeys[0] === 'highlightedLabel' && !changes['highlightedLabel'].firstChange) {
      this.applyHighlight();
      return;
    }

    this.processData();
    if (this.chartContainer) {
      this.createChart();
    }
  }

  private applyHighlight(): void {
    if (!this.renderedPaths || !this.renderedArc || !this.renderedArcHover) return;
    const label = this.highlightedLabel;
    this.renderedPaths
      .transition()
      .duration(150)
      .style('opacity', (d) => (!label || d.data.label === label ? 1 : 0.35))
      .attr('d', (d) => (this.expandOnHover && label && d.data.label === label
        ? this.renderedArcHover!(d)
        : this.renderedArc!(d)) as any);
  }

  private processData(): void {
    if (!this.data || this.data.length === 0) {
      this.displayData = [];
      return;
    }
    const total = d3.sum(this.data, (d) => d.value);

    if (this.variant === 'concentric' && this.data[0]?.segments) {
      this.displayData = this.data[0].segments.map((seg) => ({
        label: seg.label,
        color: seg.color,
        value: 0
      }));
    } else {
      // Calculate percentages for local use and template
      this.displayData = this.data.map((d) => ({
        ...d,
        percentage: total > 0 ? ((d.value / total) * 100).toFixed(1) + '%' : '0%',
      }));
    }
  }
  private createChart(): void {
    if (this.variant === 'concentric') {
      this.createConcentricChart();
    } else {
      this.createPieChart();
    }
  }

  private createConcentricChart(): void {
    // Concentric rings don't support the legend-highlight feature; drop any
    // refs from a previous pie-chart render so applyHighlight() can't touch
    // stale/removed DOM nodes.
    this.renderedPaths = undefined;
    this.renderedArc = undefined;
    this.renderedArcHover = undefined;
    const element = this.chartContainer.nativeElement;
    d3.select(element).select('svg').remove();
    // Every re-render (resize, theme change, data change) appended a fresh
    // .donut-tooltip div without removing the previous one, so stale tooltips
    // from earlier renders stayed in the DOM — leaving a stuck/duplicate
    // tooltip behind whenever a hover was active right before a re-render.
    d3.select(element).selectAll('.donut-tooltip').remove();

    if (!this.data || this.data.length === 0) return;

    const colors = this.getColors();

    let radius: number;
    let centerY: number;

    if (this.semiCircle) {
      // For semi-circle (gauge style), max radius is bound by width/2 or height
      radius = (Math.min(this.width / 2, this.height)) * this.radiusScale;
      // Center at the bottom-middle for semi-circle
      centerY = this.height * 0.9; // move slightly up from bottom to avoid cutoff
    } else {
      // Standard concentric
      radius = (Math.min(this.width, this.height) / 2) * this.radiusScale;
      centerY = this.height / 2 + this.translateY;
      if (this.arcAngle !== 360) {
        centerY += radius * 0.1; // adjust visual center slightly
      }
    }

    let startAngle = 0;
    let endAngle = 2 * Math.PI;

    if (this.semiCircle) {
      startAngle = -Math.PI / 2;
      endAngle = Math.PI / 2;
    } else if (this.arcAngle !== 360) {
      const rad = (this.arcAngle * Math.PI) / 180;
      startAngle = -rad / 2;
      endAngle = rad / 2;
    }

    const totalAngle = endAngle - startAngle;

    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .append('g')
      .attr('transform', `translate(${this.width / 2 + this.translateX}, ${centerY})`);

    // Configuration for concentric rings
    const count = this.data.length;
    const outerRadius = radius * this.outerRadiusRatio;
    const innerRadius = radius * this.innerRadiusRatio;
    const availableRadius = outerRadius - innerRadius;

    // Each item needs (ringWidth + ringGap).
    let spacePerRing = availableRadius / count;
    let ringWidth = spacePerRing * (1 - this.gapRatio);

    if (this.maxRingWidth && ringWidth > this.maxRingWidth) {
      ringWidth = this.maxRingWidth;
      // Adjust spacePerRing to keep rings tightly packed towards the outer edge, maintaining the same gap ratio relative to the new width
      spacePerRing = ringWidth / (1 - this.gapRatio);
    }

    const ringGap = spacePerRing * this.gapRatio;

    // Find max value for scaling relative to max (or user can provide max?)
    // In radial bar chart, usually one bar represents 100% which is the full circle/semicircle.
    // We can scale each bar against the maximum value in the dataset, OR against a 'total'.
    // If we want them to look like distribution where they don't necessarily sum to 100% on one track,
    // but rather are independent values, we scale by max.
    // However, if it's a "Donut" variant, usually it's parts of a whole. 
    // BUT, concentric circles visually imply independence or comparison.
    // The previous implementation used `d.value / total`.
    // Let's stick to `d.value / total` for now as it matches "Distribution". 
    // EDIT: User confirmed "Semi Concentric Circle Chart" like the image.
    // In the image, bars have different lengths. 
    // If we use d.value/total, the sum of arc lengths will be 100% of ONE ring? 
    // NO, concentric rings are separate tracks. 
    // If Green is 85% and Blue is 10%, Green track should be 85% full, Blue track 10% full.
    // So we use `percentage` for each track.

    const total = d3.sum(this.data, d => d.value);

    // Tooltip
    const tooltip = d3
      .select(element)
      .append('div')
      .attr('class', 'donut-tooltip')
      .style('position', 'absolute')
      .style('background', colors.tooltipBg)
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('min-width', '100px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', '10');

    this.data.forEach((d, i) => {
      // Outer radius of this specific ring starts from the edge and goes inward
      const currentOuterRadius = outerRadius - (i * spacePerRing);
      const currentInnerRadius = currentOuterRadius - ringWidth;

      if (currentInnerRadius <= 0) return; // Should not happen with dynamic calculation

      // Background Track (Full Circle)
      const arcTrack = d3.arc()
        .innerRadius(currentInnerRadius)
        .outerRadius(currentOuterRadius)
        .startAngle(startAngle)
        .endAngle(endAngle)
        .cornerRadius(this.roundedCaps ? ringWidth / 2 : 0);

      svg.append('path')
        .attr('d', arcTrack as any)
        .attr('fill', d.color ? `${d.color}1A` : '#D5ECEF')
        .style('cursor', d.segments && d3.sum(d.segments, s => s.value) === 0 ? 'pointer' : 'default')
        .on('mouseenter', function (event) {
          if (d.segments && d3.sum(d.segments, s => s.value) === 0) {
            d3.select(this).attr('opacity', 0.8);
            const tooltipHtml = `
             <div style="font-weight:600;color:${colors.text};margin-bottom:12px;font-size:14px;">${d.label}</div>
             ${d.segments.map(s => `
               <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;font-size:13px;color:${colors.tooltipText};">
                 <div style="width:8px;height:8px;border-radius:50%;background-color:${s.color}"></div>
                 <span style="display:flex;justify-content:space-between;width:100%;">${s.label}: <span>0%</span></span>
               </div>
             `).join('')}
            `;
            tooltip.style('opacity', 1).html(tooltipHtml);
          }
        })
        // .on('mousemove', function (event) {
        //   if (d.segments && d3.sum(d.segments, s => s.value) === 0) {
        //     tooltip
        //       .style('left', event.offsetX + 15 + 'px')
        //       .style('top', event.offsetY - 10 + 'px');
        //   }
        // })
        .on('mousemove', (event) => {
          this.setTooltipPosition(tooltip, event, element);
        })
        .on('mouseleave', function () {
          if (d.segments && d3.sum(d.segments, s => s.value) === 0) {
            d3.select(this).attr('opacity', 1);
            tooltip.style('opacity', 0);
          }
        });

      if (d.segments && d.segments.length > 0) {
        const ringTotal = d3.sum(d.segments, s => s.value);
        let currentAngle = startAngle;

        const pieGenerator = d3.pie<{ label: string; value: number; color?: string }>()
          .value(s => s.value)
          .startAngle(startAngle)
          .endAngle(endAngle)
          .sort(null); // Keep array order

        const pieData = pieGenerator(d.segments || []);

        // Create a unique ID for this ring's clip path
        const clipId = `ring-clip-${Math.random().toString(36).substr(2, 9)}`;

        // Define the clip path arc (full span of the data for this ring, rounded at the ends)
        const clipArc = d3.arc()
          .innerRadius(currentInnerRadius)
          .outerRadius(currentOuterRadius)
          .cornerRadius(this.roundedCaps ? ringWidth / 2 : 0)
          .startAngle(startAngle)
          .endAngle(endAngle);

        const defs = svg.append('defs');
        defs.append('clipPath')
          .attr('id', clipId)
          .append('path')
          .attr('d', clipArc as any);

        const ringGroup = svg.append('g')
          .attr('clip-path', `url(#${clipId})`);

        d.segments.forEach(seg => {
          const valueFraction = ringTotal > 0 ? seg.value / ringTotal : 0;
          const segmentAngle = valueFraction * totalAngle;

          if (segmentAngle === 0) return;

          const segEndAngle = currentAngle + segmentAngle;

          const arcSegment = d3.arc()
            .innerRadius(currentInnerRadius)
            .outerRadius(currentOuterRadius)
            .cornerRadius(0) // Internal segments should have 0 corner radius to stack seamlessly
            .startAngle(currentAngle)
            .endAngle(segEndAngle);

          ringGroup.append('path')
            .attr('d', arcSegment as any)
            .attr('fill', seg.color as string)
            .style('cursor', 'pointer')
            .on('mouseenter', function (event) {
              d3.select(this).attr('opacity', 0.8);
              const tooltipHtml = `
                  <div style="font-weight:600;color:${colors.text};margin-bottom:12px;font-size:14px;">${d.label}</div>
                  ${d.segments!.map(s => `
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;font-size:13px;color:${colors.tooltipText};">
                      <div style="width:8px;height:8px;border-radius:50%;background-color:${s.color}"></div>
                      <span style="display:flex;justify-content:space-between;width:100%;">${s.label}: <span>${s.value}%</span></span>
                    </div>
                  `).join('')}
                 `;
              tooltip.style('opacity', 1).html(tooltipHtml);
            })
            // .on('mousemove', function (event) {
            //   tooltip
            //     .style('left', event.offsetX + 15 + 'px')
            //     .style('top', event.offsetY - 10 + 'px');
            // })
            .on('mousemove', (event) => {
              this.setTooltipPosition(tooltip, event, element);
            })
            .on('mouseleave', function () {
              d3.select(this).attr('opacity', 1);
              tooltip.style('opacity', 0);
            });

          currentAngle = segEndAngle;
        });

      } else {
        // Value Arc
        const valueFractionBase = total > 0 ? d.value / total : 0;
        const targetAngle = startAngle + (valueFractionBase * totalAngle);

        const arcValue = d3.arc()
          .innerRadius(currentInnerRadius)
          .outerRadius(currentOuterRadius)
          .startAngle(startAngle)
          .endAngle(targetAngle)
          .cornerRadius(this.roundedCaps ? ringWidth / 2 : 0);

        svg.append('path')
          .attr('d', arcValue as any)
          .attr('fill', d.color as string)
          .style('cursor', 'pointer')
          .on('mouseenter', function (event) {
            d3.select(this).attr('opacity', 0.8);
            tooltip.style('opacity', 1).html(`
                <div style="font-weight:600;color:${d.labelColor || d.color};margin-bottom:2px;">${d.label}</div>
                <div style="color:${colors.tooltipText};">Count: <b>${d.value}</b></div>
               `);
          })
          // .on('mousemove', function (event) {
          //   tooltip
          //     .style('left', event.offsetX + 15 + 'px')
          //     .style('top', event.offsetY - 10 + 'px');
          // })
          .on('mousemove', (event) => {
            if (d.segments && d3.sum(d.segments, s => s.value) === 0) {
              this.setTooltipPosition(tooltip, event, element);
            }
          })
          .on('mouseleave', function () {
            d3.select(this).attr('opacity', 1);
            tooltip.style('opacity', 0);
          });
      }
    });
  }

  private createPieChart(): void {
    // Reset highlight refs up front; they're reassigned below once slices
    // exist, but an early return (e.g. empty data) must not leave stale refs
    // pointing at DOM nodes this render is about to remove.
    this.renderedPaths = undefined;
    this.renderedArc = undefined;
    this.renderedArcHover = undefined;
    const element = this.chartContainer.nativeElement;
    d3.select(element).select('svg').remove(); // Clear previous chart
    // See createConcentricChart: without this, stale tooltip divs from earlier
    // renders never get removed and can be left stuck visible on screen.
    d3.select(element).selectAll('.donut-tooltip').remove();
    const colors = this.getColors();
    const radius = (Math.min(this.width, this.height) / 2) * this.radiusScale;
    const innerRadius = radius * this.innerRadiusRatio; // Creates the "Donut" hole
    const outerRadius = radius * this.outerRadiusRatio;
    // const innerRadius = radius * 0.45; // Creates the "Donut" hole
    // const outerRadius = radius * 0.7;

    // 1. Setup SVG
    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', '100%') // Responsive width
      .attr('height', '100%') // Responsive height
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .append('g')
      .attr('transform', `translate(${this.width / 2 + this.translateX}, ${this.height / 2 + this.translateY})`);

    // 2. Setup Data & Pie Generator
    const filteredData = this.data.filter(d => d.value > 0);
    const total = d3.sum(filteredData, (d) => d.value);

    // If no data to show, don't render slices
    if (filteredData.length === 0 && total === 0) return;

    const pie = d3
      .pie<ChartData>()
      .value((d) => d.value)
      .sort(null); // Keep order as provided

    // Tooltip
    const tooltip = d3
      .select(element)
      .append('div')
      .attr('class', 'donut-tooltip')
      .style('position', 'absolute')
      .style('background', colors.tooltipBg)
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // Normal arc
    const arc = d3
      .arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);

    // Hover arc (slightly larger)
    const arcHover = d3
      .arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius + 10);

    // Label Arc (Outside Position 1 - Near)
    const labelArcNear = d3
      .arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(radius * 0.8)
      .outerRadius(radius * 0.8);

    // Label Arc (Outside Position 2 - Far)
    const labelArcFar = d3
      .arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    // 3. Draw Slices
    const arcs = svg
      .selectAll('path')
      .data(pie(filteredData))
      .enter()
      .append('g')
      .attr('class', 'slice');

    // Segment separator: match the actual card surface color instead of an
    // arbitrary dark navy, so the gap blends into the card in dark mode
    // instead of reading as a stray black outline. Kept thin for subtlety.
    const sliceStroke = this.strokeColor
      ? this.strokeColor
      : this.currentTheme === 'dark'
        ? colors.tooltipBg // matches the dark card surface (#1E293B)
        : '#fff';

    const paths = arcs
      .append('path')
      .attr('fill', (d) => d.data.color || '#cccccc')
      .attr('stroke', sliceStroke) // Stroke matches background (gap effect)
      .style('stroke-width', '1.5px')
      .style('cursor', 'pointer')
      .style('transition', 'opacity 0.2s')
      // Start each slice collapsed to zero length at its own start angle so the
      // entry transition below can sweep it open — this is what makes the ring
      // "draw" into place instead of appearing all at once.
      .attr('d', (d) => arc({ ...d, endAngle: d.startAngle } as any) as string);

    // Hover handlers are bound to the plain (non-transition) selection so the
    // tooltip works immediately, during, and after the entry animation.
    paths
      // HOVER IN
      .on('mouseenter', (event, d) => {
        // Let a consumer (e.g. a legend rendered outside this component) mirror the highlight.
        this.sliceHover.emit(d.data.label);

        // Dim every other slice so hovering a ring segment highlights it the
        // same way hovering the matching legend row does.
        paths
          .filter((pd: any) => pd !== d)
          .transition()
          .duration(150)
          .style('opacity', 0.35);

        // Expand the slice on hover
        const transition = d3.select(event.currentTarget)
          .transition()
          .duration(200)
          .style('opacity', 1);

        if (this.expandOnHover) {
          transition.attr('d', arcHover as any);
        }

        const percent = ((d.data.value / total) * 100).toFixed(1);

        const html = this.customTooltip ? this.customTooltip(d, percent, colors) : `
        <div style="font-weight:500;margin-bottom:4px;color:${d.data.labelColor || d.data.color || '#615f5fff'};">
          ${d.data.label}
        </div>
        <div style="display:flex; align-items: center; gap: 30px;">
         <div style="font-size:16px;font-weight:400;margin-bottom:2px;color:${colors.tooltipText
          }">${d.data.count !== undefined ? d.data.count.toLocaleString() : d.data.value.toLocaleString()}</div>
        <div style="opacity:0.8;font-size:14px;color:${colors.tooltipText
          }">${percent}%</div>
        </div>
      `;

        tooltip.style('opacity', 1).html(html);
      })

      // MOVE TOOLTIP
      // .on('mousemove', function (event) {
      //   tooltip
      //     .style('left', event.offsetX + 15 + 'px')
      //     .style('top', event.offsetY - 10 + 'px');
      // })
      .on('mousemove', (event) => {
        this.setTooltipPosition(tooltip, event, element);
      })
      // HOVER OUT
      .on('mouseleave', (event) => {
        this.sliceHover.emit(null);

        // Restore every slice's opacity, not just the one that was hovered.
        paths
          .transition()
          .duration(150)
          .style('opacity', 1);

        const transition = d3.select(event.currentTarget)
          .transition()
          .duration(200);

        if (this.expandOnHover) {
          transition.attr('d', arc as any);
        }

        tooltip.style('opacity', 0);
      });

    // Progressive circular draw: each slice sweeps open from its start angle to
    // its final end angle, staggered slice-to-slice so the ring fills in
    // sequentially around the circle rather than popping in all at once.
    paths
      .transition()
      .duration(650)
      .delay((_d, i) => i * 120)
      .ease(d3.easeCubicOut)
      .attrTween('d', function (d: any) {
        const interpolateEndAngle = d3.interpolate(d.startAngle, d.endAngle);
        return (t: number) => arc({ ...d, endAngle: interpolateEndAngle(t) } as any) as string;
      });

    // Keep refs so an external legend-hover highlight can restyle slices later.
    this.renderedPaths = paths;
    this.renderedArc = arc;
    this.renderedArcHover = arcHover;
    if (this.highlightedLabel) {
      this.applyHighlight();
    }

    // 4. Add Percentage Labels on Chart
    if (this.showValue) {
      const radius = (Math.min(this.width, this.height) / 2) * this.radiusScale;
      // Use consistent scaled radius
      const outerRadius = radius * this.outerRadiusRatio;

      const arcLabel = d3
        .arc<d3.PieArcDatum<ChartData>>()
        .innerRadius(outerRadius)
        .outerRadius(outerRadius);

      const outerArc = d3
        .arc<d3.PieArcDatum<ChartData>>()
        .innerRadius(outerRadius + 10)
        .outerRadius(outerRadius + 10);

      // labelOnArc: project the label radially outward along each slice's
      // angle so it sits clear of the donut edge instead of overlapping it.
      // Tiny slices (e.g. 1%) need extra clearance so the percentage text
      // doesn't visually touch or sit on top of the donut stroke.
      const labelArcRadius = outerRadius + 32;
      const labelArc = d3
        .arc<d3.PieArcDatum<ChartData>>()
        .innerRadius(labelArcRadius)
        .outerRadius(labelArcRadius);

      const pieData = pie(filteredData);
      const total = d3.sum(filteredData, (d) => d.value);

      // Prepare data for layout
      const labelData = pieData.map((d) => {
        const percent = (d.data.value / total) * 100;
        const pos = outerArc.centroid(d);
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        // Determine side: Right if midAngle < PI, Left otherwise
        const isRight = midAngle < Math.PI;

        // Initial label position
        // When polylines are hidden, place labels much closer to the donut
        const labelOffset = this.showPolylines ? 20 : 5;
        // labelOnArc: anchor the label on a label-arc that sits outside the
        // donut along the slice angle, then nudge horizontally to clear the
        // chart border. Without labelOnArc, fall back to the original
        // edge-aligned behaviour.
        const arcPos = this.labelOnArc ? labelArc.centroid(d) : pos;
        // Bigger horizontal nudge keeps narrow-slice labels from sitting on
        // the donut edge when their centroid is almost vertical.
        const labelX = this.labelOnArc
          ? arcPos[0] + (isRight ? 12 : -12)
          : isRight ? outerRadius + labelOffset : -outerRadius - labelOffset;
        const labelY = this.labelOnArc ? arcPos[1] : pos[1];

        return {
          d,
          percent,
          isRight,
          initialY: labelY,
          x: labelX,
          y: labelY, // Will be adjusted
          posA: arc.centroid(d), // Start of line (slice center)
          posB: outerArc.centroid(d), // Knee of the dogleg
          color: d.data.color || '#cccccc',
          text: this.labelFormat === 'value-percentage'
            ? `${d.data.value.toLocaleString()} (${percent.toFixed(1)}%)`
            : this.labelFormat === 'percentage'
              ? `${percent.toFixed(0)}%`
              : `${d.data.label}: ${percent.toFixed(0)}%`,
        };
      });

      // Show labels for slices > 1% (or keep user's logic) - user asked to handle small slices, so we show them but maybe skip effectively 0
      const labelsToShow = labelData.filter((l) => l.percent > 0);

      // Separate into left and right
      const rightLabels = labelsToShow
        .filter((l) => l.isRight)
        .sort((a, b) => a.initialY - b.initialY); // Sort top-to-bottom
      const leftLabels = labelsToShow
        .filter((l) => !l.isRight)
        .sort((a, b) => a.initialY - b.initialY);

      // Label height / spacing — give a little more breathing room when
      // labels are placed directly next to the slices (labelOnArc) so two
      // adjacent percentages don't visually collide.
      const spacing = this.labelOnArc ? 26 : 20;

      // Relax function to prevent overlap
      const relax = (group: typeof labelData) => {
        for (let i = 0; i < group.length; i++) {
          const l1 = group[i];
          // Check collision with previous
          if (i > 0) {
            const l0 = group[i - 1];
            if (l1.y - l0.y < spacing) {
              // Overlap detected. Push l1 down.
              // Optional: Push l0 up a bit too? Simple sweep is usually fine.
              l1.y = l0.y + spacing;
            }
          }
        }
      };

      relax(rightLabels);
      relax(leftLabels);

      // Combine back
      const allAdjustedLabels = [...rightLabels, ...leftLabels];

      // Draw Polylines
      if (this.showPolylines) {
        svg
          .selectAll('polyline')
          .data(allAdjustedLabels)
          .enter()
          .append('polyline')
          .attr('points', (d) => {
            // posA -> posB -> posC
            const p = [d.posA, d.posB, [d.x, d.y]];
            return p.map((point) => point.join(',')).join(' ');
          })
          .style('fill', 'none')
          .style('stroke', (d) => d.color)
          .style('stroke-width', 1)
          .style('opacity', 0.5);
      }

      // Draw Text
      svg
        .selectAll('text')
        .data(allAdjustedLabels)
        .enter()
        .append('text')
        .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
        .attr('dy', '0.35em')
        .text((d) => d.text)
        .attr('text-anchor', (d) => (d.isRight ? 'start' : 'end'))
        .style('fill', (d) => d.color)
        .style('font-size', '14px') // Slightly smaller for better fit
        .style('font-weight', 'bold');
    }
  }
  // ADD THIS METHOD INSIDE YOUR COMPONENT CLASS
  // Place it just above: private getColors()

  private setTooltipPosition(
    tooltip: any,
    event: any,
    element: HTMLElement
  ): void {
    const tooltipNode = tooltip.node();

    if (!tooltipNode) return;

    const isMobile = window.innerWidth <= 768;

    // Mobile responsive tooltip
    if (isMobile) {
      tooltip
        .style('max-width', '140px')
        .style('font-size', '12px')
        .style('padding', '6px')
        .style('white-space', 'normal')
        .style('word-break', 'break-word')
        .style('line-height', '1.4');
    } else {
      tooltip
        .style('max-width', '220px')
        .style('font-size', '14px')
        .style('padding', '8px')
        .style('white-space', 'nowrap');
    }

    const tooltipRect = tooltipNode.getBoundingClientRect();
    const containerRect = element.getBoundingClientRect();

    let left = event.offsetX + 15;
    let top = event.offsetY - 10;

    // Better mobile positioning
    if (isMobile) {
      left = event.offsetX - tooltipRect.width / 2;
      top = event.offsetY - tooltipRect.height - 14;
    }

    // These clamps keep the tooltip inside the chart's own container — but a
    // compact chart (e.g. a small donut) can be narrower/shorter than the
    // tooltip itself, in which case forcing it to "fit" would push it back
    // onto the chart, covering the center total. Only clamp to the container
    // when the container can actually hold the tooltip; otherwise just stop
    // it drifting off the top/left edge and let it overflow past the chart
    // into the surrounding page instead of over the donut.
    const canFitWidth = containerRect.width >= tooltipRect.width + 20;
    const canFitHeight = containerRect.height >= tooltipRect.height + 20;

    if (canFitWidth) {
      // Prevent right overflow
      if (left + tooltipRect.width > containerRect.width) {
        left = containerRect.width - tooltipRect.width - 10;
      }
      // Prevent left overflow
      if (left < 10) {
        left = 10;
      }
    } else if (left < 0) {
      left = 0;
    }

    if (canFitHeight) {
      // Prevent bottom overflow
      if (top + tooltipRect.height > containerRect.height) {
        top = containerRect.height - tooltipRect.height - 10;
      }
      // Prevent top overflow
      if (top < 10) {
        top = 10;
      }
    } else if (top < 0) {
      top = 0;
    }

    tooltip
      .style('left', `${left}px`)
      .style('top', `${top}px`);
  }
  private getColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      text: isDark ? '#e4e4e7' : '#1E293B',
      axisLine: isDark ? '#47474e' : '#CBD5E1',
      gridLine: isDark ? '#2e2e33' : '#E2E8F0',
      tooltipBg: isDark ? '#1e1e21' : '#FFFFFF',
      tooltipBorder: isDark ? '#2e2e33' : '#E2E8F0',
      tooltipText: isDark ? '#fafafa' : '#0F172A',
      primary: '#3B82F6',
    };
  }
}
